import { HydratedAssetShare } from "@zapier/client";
import { Avatar, Text } from "@zapier/design-system";
import { useTranslations } from "@zapier/i18n";
import styles from "./ShareList.module.css";

interface ShareEntityProps {
  imageUrl: string | null | undefined;
  label: React.ReactNode;
  name: string;
}

/** Returns the props for the share entity based on the subject type */
export function useShareEntityProps(
  share: HydratedAssetShare,
): ShareEntityProps | undefined {
  const t = useTranslations("share-entity");

  if (!share[share.subjectType]) {
    return;
  }

  switch (share.subjectType) {
    case "account": {
      if (share.group === "admin") {
        const label = share.account?.name
          ? t.rich("labels.admins-at", {
              accountName: share.account.name,
              prefix: (chunks) => chunks,
              accountNameTag: (chunks) => <Text type="Body3">{chunks}</Text>,
            })
          : t("labels.admins-on-account");
        return {
          label: share.account?.name ? (
            <Text type="Body1">{label}</Text>
          ) : (
            label
          ),
          name: share.account?.name ?? t("fallbacks.account"),
          imageUrl: share.account?.logoUrl,
        };
      }

      const label = share.account?.name
        ? t.rich("labels.everyone-at", {
            accountName: share.account.name,
            prefix: (chunks) => chunks,
            accountNameTag: (chunks) => <Text type="Body3">{chunks}</Text>,
          })
        : t("labels.everyone-on-account");

      return {
        label: share.account?.name ? <Text type="Body1">{label}</Text> : label,
        name: share.account?.name ?? t("fallbacks.account"),
        imageUrl: share.account?.logoUrl,
      };
    }
    case "team": {
      return {
        label: share.team?.name ?? t("fallbacks.team"),
        name: share.team?.name ?? t("fallbacks.team"),
        imageUrl: share.team?.logo,
      };
    }
    case "user": {
      return {
        label: share.user?.fullName ?? t("fallbacks.user"),
        name: share.user?.fullName ?? t("fallbacks.user"),
        imageUrl: share.user?.photoUrl,
      };
    }
  }
}

export function ShareEntity({ imageUrl, label, name }: ShareEntityProps) {
  return (
    <div className={styles.entity}>
      <Avatar size="medium" name={name} url={imageUrl} />
      <Text color="TextDefault" type="Body3">
        {label}
      </Text>
    </div>
  );
}
